/**
 * QuoteRequestListPage - Presentation Layer
 *
 * Displays list of quote requests with actions to respond or start chat.
 * Uses application hooks for high-level operations.
 */

import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ProjectCardHorizontal,
  ProjectFilterSection,
  Switch,
  Label,
  cn,
} from "@semo/shared-ui";
import {
  ArrowRight,
  MessageCircle,
  Package,
  AlertCircle,
  Loader2,
  Star,
} from "@semo/icons";
import { useQuoteRequestOperations } from "../../application/hooks/useQuoteRequestOperations";
import { useProjectBookmarks } from "../../application/hooks/useProjectBookmarks";
import { BookmarkStar } from "../components/BookmarkStar";
import type { QuoteRequestListItemDto } from "@semo/api-contracts";
import { useDebounce } from "@semo/shared-utils";
import { toast } from "sonner";
import {
  isQuoteRequestClosed,
  QUOTE_REQUEST_CLOSED_MESSAGE,
} from "../../domain/quoteRequestClosed";

export interface QuoteRequestListPageProps {
  /**
   * Optional custom handler for project click (show details)
   */
  onProjectClick?: (requestId: string) => void;
  /**
   * Optional custom handler for opening chat.
   * If not provided, uses the default openChatFromRequest hook.
   */
  onChatOpen?: (requestId: string) => Promise<void>;
  /**
   * Optional custom handler for opening quote response.
   * If not provided, uses the default openQuoteResponse hook.
   */
  onRespondOpen?: (requestId: string) => Promise<void>;
  /**
   * DEV 전용: 실제 API 대신 샘플 공고로 렌더 (/dev/preview).
   * 값이 있으면 서버 데이터/로딩/에러/무한스크롤을 건너뛴다.
   */
  previewProjects?: QuoteRequestListItemDto[];
}

const SEARCH_KEYWORDS = [
  "전체",
  "인테리어",
  "가구",
  "사무실",
  "철거",
  "청소",
  "앱개발",
  "디자인",
  "CCTV",
  "전기공사",
  "화물",
  "중고차",
];

// 상태 탭 (씨마켓 스타일): 카드의 status 뱃지와 동일 규칙.
// 공급사 API 는 마감을 `isClosed` 로 따로 주고 «대기» 상태는 내려 주지 않는다 — 늘 0 이던
// «대기» 탭은 뺐다.
const STATUS_TABS = [
  { key: "ALL", label: "전체" },
  { key: "IN_PROGRESS", label: "진행중" },
  { key: "CLOSED", label: "마감" },
] as const;

type StatusTabKey = (typeof STATUS_TABS)[number]["key"];

/** 상태 탭 → 서버 `isClosed` 거름(전체면 거르지 않음). 쪽마다가 아니라 서버 전체에서 거른다. */
function isClosedParamOf(tab: StatusTabKey): boolean | undefined {
  if (tab === "IN_PROGRESS") return false;
  if (tab === "CLOSED") return true;
  return undefined;
}

/** 카드 뱃지용 — 마감이면 «마감», 아니면 진행 상태 그대로. */
function withDisplayStatus(
  request: QuoteRequestListItemDto,
): QuoteRequestListItemDto {
  return isQuoteRequestClosed(request)
    ? { ...request, status: "CLOSED" }
    : request;
}

export function QuoteRequestListPage({
  onProjectClick,
  onChatOpen,
  onRespondOpen,
  previewProjects,
}: QuoteRequestListPageProps) {
  const queryClient = useQueryClient();
  const observerTarget = useRef<HTMLDivElement>(null);
  const isPreview = previewProjects !== undefined;

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sortBy, setSortBy] = useState<"created_at" | "deadline">("created_at");
  const [unrespondedOnly, setUnrespondedOnly] = useState(false);
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusTabKey>("ALL");

  const {
    count: bookmarkCount,
    isBookmarked,
    toggle: toggleBookmark,
  } = useProjectBookmarks();

  const [processingId, setProcessingId] = useState<string | null>(null);

  const {
    requests,
    statusSummary,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    openQuoteResponse,
    openChatFromRequest,
  } = useQuoteRequestOperations(
    {
      search: debouncedSearch,
      sortBy: sortBy,
      sortOrder: sortBy === "deadline" ? "asc" : "desc",
      isClosed: isPreview ? undefined : isClosedParamOf(statusFilter),
    },
    { limit: 12 },
  );

  // 표시할 데이터 소스: 프리뷰면 샘플, 아니면 서버 데이터
  const sourceRequests = previewProjects ?? requests;

  // Infinite Scroll Observer (프리뷰에서는 비활성)
  useEffect(() => {
    if (isPreview) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry &&
          entry.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isPreview]);

  // 상태 탭을 제외한 필터 (관심공고 + 미참여만) — 상태별 카운트의 기준
  const baseFiltered = useMemo(() => {
    return sourceRequests.filter((r) => {
      if (showBookmarkedOnly && !isBookmarked(r.id)) return false;
      // 미참여만 보기: 이미 참여(견적 제출)한 공고는 숨김 (참여한 건 '참여 프로젝트' 탭에)
      if (unrespondedOnly && r.hasResponded) return false;
      return true;
    });
  }, [sourceRequests, unrespondedOnly, showBookmarkedOnly, isBookmarked]);

  // 관심공고·미참여만 은 화면에서만 거르는 조건이다 — 이때 서버 숫자를 그대로 붙이면 목록과
  // 어긋나므로 숫자를 숨긴다.
  const hasLocalOnlyFilter = showBookmarkedOnly || unrespondedOnly;

  // 상태 탭 숫자 (A3-10): 서버가 검색 조건 전체로 센 값. 불러온 쪽 수로 세지 않는다.
  // 미리보기(샘플)만 샘플 목록으로 센다.
  const statusCounts = useMemo((): Record<StatusTabKey, number> | null => {
    if (isPreview) {
      const closed = baseFiltered.filter((r) => isQuoteRequestClosed(r)).length;
      return {
        ALL: baseFiltered.length,
        IN_PROGRESS: baseFiltered.length - closed,
        CLOSED: closed,
      };
    }
    if (!statusSummary || hasLocalOnlyFilter) return null;
    return {
      ALL: statusSummary.all,
      IN_PROGRESS: statusSummary.ongoing,
      CLOSED: statusSummary.closed,
    };
  }, [isPreview, baseFiltered, statusSummary, hasLocalOnlyFilter]);

  // 최종 목록: 상태 탭은 서버가 이미 걸렀다(미리보기만 여기서 거른다). 마감 공고는 «마감» 뱃지.
  const filteredRequests = useMemo(() => {
    const byTab =
      isPreview && statusFilter !== "ALL"
        ? baseFiltered.filter(
            (r) => isQuoteRequestClosed(r) === (statusFilter === "CLOSED"),
          )
        : baseFiltered;
    return byTab.map(withDisplayStatus);
  }, [baseFiltered, statusFilter, isPreview]);

  const handleRespondClick = useCallback(
    async (request: QuoteRequestListItemDto) => {
      if (processingId) return;
      if (isQuoteRequestClosed(request)) {
        toast.info(QUOTE_REQUEST_CLOSED_MESSAGE);
        return;
      }
      setProcessingId(request.id);
      try {
        if (onRespondOpen) {
          await onRespondOpen(request.id);
        } else {
          await openQuoteResponse(request.id);
        }
      } finally {
        setProcessingId(null);
      }
    },
    [onRespondOpen, openQuoteResponse, processingId],
  );

  const handleChatClick = useCallback(
    async (request: QuoteRequestListItemDto) => {
      if (processingId) return;
      setProcessingId(request.id);
      try {
        if (onChatOpen) {
          await onChatOpen(request.id);
        } else {
          await openChatFromRequest(request.id);
        }
      } finally {
        setProcessingId(null);
      }
    },
    [onChatOpen, openChatFromRequest, processingId],
  );

  const handleCardClick = useCallback(
    (request: QuoteRequestListItemDto) => {
      if (onProjectClick) {
        onProjectClick(request.id);
      } else {
        // Fallback behavior if no project click handler provided
        if (request.hasResponded) {
          handleChatClick(request);
        } else {
          handleRespondClick(request);
        }
      }
    },
    [onProjectClick, handleChatClick, handleRespondClick],
  );

  if (!isPreview && isLoading) {
    return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-32 bg-slate-50 animate-pulse rounded-2xl w-full border border-slate-100"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!isPreview && error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-slate-600">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 h-full overflow-y-auto">
      {/* 헤딩 + 설명 — 원가계산서/상품·단가 관리와 톤 통일 */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">
          프로젝트 찾기
        </h1>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">
          참여 가능한 견적 요청(공고)을 찾아 견적에 참여해보세요.
        </p>
      </div>

      <ProjectFilterSection
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filterStatuses={[]}
        onFilterStatusesChange={() => {}}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        recommendedKeywords={SEARCH_KEYWORDS}
        // 참여 여부는 드롭다운 대신 아래 토글로 대체 → 옵션 비워서 드롭다운 숨김
        filterOptions={[]}
        sortOptions={[
          { key: "created_at", label: "최신순" },
          { key: "deadline", label: "마감일순" },
        ]}
      />

      {/* 상태 탭(전체/진행중/대기/마감) + 관심공고 필터 */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        {/* 상태별 탭 — 씨마켓 스타일 세그먼트 */}
        <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
          {STATUS_TABS.map((tab) => {
            const active = statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                aria-pressed={active}
                onClick={() => setStatusFilter(tab.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
                  active
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {tab.label}
                {statusCounts && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-2xs font-semibold leading-none",
                      active
                        ? "bg-primary/10 text-primary"
                        : "bg-slate-200/70 text-slate-500",
                    )}
                  >
                    {statusCounts[tab.key]}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          {/* 미참여만 보기 토글 — 상품/단가 "내 상품만 보기"와 동일 UX.
              참여한 공고는 '참여 프로젝트' 탭에 있으므로 여긴 새 기회만 걸러본다. */}
          <div className="flex items-center gap-2 shrink-0">
            <Switch
              id="unresponded-only-filter"
              checked={unrespondedOnly}
              onCheckedChange={setUnrespondedOnly}
            />
            <Label
              htmlFor="unresponded-only-filter"
              className={cn(
                "text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors",
                unrespondedOnly ? "text-primary" : "text-slate-500",
              )}
            >
              미참여만 보기
            </Label>
          </div>

          {/* 관심공고 필터 알약(pill) — 카드 별표와 색/아이콘 통일 */}
          <button
            type="button"
            aria-pressed={showBookmarkedOnly}
            onClick={() => setShowBookmarkedOnly((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all active:scale-95",
              showBookmarkedOnly
                ? "border-primary bg-primary/10 text-primary shadow-sm"
                : "border-slate-200 bg-white text-slate-500 hover:border-primary/40 hover:text-primary/70",
            )}
          >
            <Star
              className={cn(
                "w-4 h-4",
                showBookmarkedOnly ? "fill-primary" : "fill-transparent",
              )}
              strokeWidth={showBookmarkedOnly ? 2 : 1.8}
            />
            관심공고
            <span
              className={cn(
                "ml-0.5 rounded-full px-1.5 py-0.5 text-2xs font-semibold leading-none",
                showBookmarkedOnly
                  ? "bg-primary text-primary-foreground"
                  : "bg-slate-100 text-slate-500",
              )}
            >
              {bookmarkCount}
            </span>
          </button>
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          {showBookmarkedOnly ? (
            <>
              <Star className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">관심공고가 없습니다.</p>
              <p className="text-slate-400 text-sm mt-1">
                공고 왼쪽의 별(☆)을 눌러 관심공고로 저장해보세요.
              </p>
            </>
          ) : (
            <>
              <Package className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">
                {statusFilter === "CLOSED"
                  ? "마감된 견적 요청이 없습니다."
                  : statusFilter === "IN_PROGRESS"
                    ? "진행 중인 견적 요청이 없습니다."
                    : "견적 요청이 없습니다."}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((req) => (
            <ProjectCardHorizontal
              key={req.id}
              project={req}
              onClick={() => handleCardClick(req)}
              leadingSlot={
                <BookmarkStar
                  active={isBookmarked(req.id)}
                  onToggle={() => toggleBookmark(req.id)}
                  label={req.projectName}
                />
              }
              action={
                <div className="flex items-center gap-2">
                  {req.hasResponded ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChatClick(req);
                      }}
                      disabled={processingId === req.id}
                      className="px-4 py-2 bg-primary/5 text-primary border border-primary/20 rounded-lg text-sm font-semibold hover:bg-primary/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === req.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <span>채팅하기</span>
                          <MessageCircle className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  ) : req.status === "CLOSED" ? (
                    // 마감된 공고는 참여 버튼 대신 «마감» 표시 — 서버도 새 견적을 받지 않는다.
                    <span
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-400 bg-slate-100 border border-slate-200 cursor-default"
                      aria-label="마감된 견적 요청"
                    >
                      마감
                    </span>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRespondClick(req);
                      }}
                      disabled={processingId === req.id}
                      className="px-4 py-2 bg-primary hover:bg-primary-hover text-primary-foreground border border-transparent rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingId === req.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <span>참여하기</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              }
            />
          ))}
        </div>
      )}

      {/* Infinite Scroll Trigger */}
      <div
        ref={observerTarget}
        className="w-full py-8 flex items-center justify-center"
      >
        {isFetchingNextPage && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>불러오는 중...</span>
          </div>
        )}
        {!hasNextPage && filteredRequests.length > 0 && (
          <p className="text-slate-400 text-sm">모든 요청을 불러왔습니다.</p>
        )}
      </div>
    </div>
  );
}
